import { resolveAssetUrl } from "@/lib/api";
import { formatDate } from "@/lib/statusUtils";

const DIR_LBL = {
  F: "Full plot", N: "North", S: "South", E: "East", W: "West",
  NE: "North-East", NW: "North-West", SE: "South-East", SW: "South-West",
  P1: "Portion 1", P2: "Portion 2", P3: "Portion 3", P4: "Portion 4",
};
const inr = (n) => (n == null || n === "") ? "—" : `₹${Number(n).toLocaleString("en-IN")}`;

/**
 * Print-only view of an approved booking.
 * Injected off-screen; `window.print()` uses the `@media print` CSS in App.css
 * to show only this element.
 */
export default function BookingPrintView({ booking }) {
  if (!booking) return null;
  const isSketchPdf = booking.sketch_url && /\.pdf($|\?)/i.test(booking.sketch_url);
  return (
    <div id="booking-print-root" className="print-only bg-white text-black p-8" data-testid="booking-print-view">
      <div className="text-center border-b-2 border-black pb-3 mb-4">
        <div className="text-xs tracking-widest uppercase">CitySquare Infra</div>
        <div className="text-2xl font-semibold mt-1">Booking Confirmation</div>
        <div className="text-xs text-gray-700 mt-1">Anuttara Township Phase 2 @ Kamareddy</div>
      </div>

      <div className="flex justify-between text-xs mb-4">
        <div>
          <div><b>Booking #</b> {booking.booking_no}</div>
          <div><b>Status</b> APPROVED</div>
          {booking.passbook_no && <div><b>Passbook #</b> {booking.passbook_no}</div>}
        </div>
        <div className="text-right">
          <div><b>Approved by</b> {booking.approved_by_name || "—"}</div>
          <div><b>Approved on</b> {formatDate(booking.approved_at)}</div>
          <div><b>Requested by</b> {booking.requested_by_name}</div>
        </div>
      </div>

      <Section title="Plot">
        <Row k="Project" v="Anuttara Township Phase 2 @ Kamareddy" />
        <Row k="Phase / LP" v={`${booking.phase_name} · ${booking.lp_no}`} />
        <Row k="Plot #" v={booking.plot_no} />
        {booking.portion_code && <Row k="Portion Code" v={booking.portion_code} />}
        {booking.direction && <Row k="Direction" v={DIR_LBL[booking.direction] || booking.direction} />}
        {booking.area_sqyd != null && <Row k="Area (sq.yd)" v={Number(booking.area_sqyd).toFixed(2)} />}
      </Section>

      {booking.boundaries && (
        <Section title="Boundaries">
          <Row k="North" v={booking.boundaries.north || "—"} />
          <Row k="South" v={booking.boundaries.south || "—"} />
          <Row k="East" v={booking.boundaries.east || "—"} />
          <Row k="West" v={booking.boundaries.west || "—"} />
        </Section>
      )}

      <Section title="Customer">
        <Row k="Name" v={booking.customer_name} />
        <Row k="Mobile" v={booking.customer_mobile} />
        <Row k="Email" v={booking.customer_email || "—"} />
        <Row k="Address" v={booking.customer_address || "—"} />
        <Row k="ID Reference" v={booking.id_reference || "—"} />
      </Section>

      <Section title="Payment">
        <Row k="Quoted Price" v={inr(booking.quoted_price)} />
        <Row k="Amount Paid" v={inr(booking.amount_paid)} />
        <Row k="Payment Mode" v={booking.payment_mode || "—"} />
        <Row k="Payment Option" v={(booking.payment_option || "full").toUpperCase()} />
        <Row k="Payment Date" v={booking.payment_date || "—"} />
      </Section>

      {booking.notes && (
        <Section title="Notes">
          <div className="text-xs">{booking.notes}</div>
        </Section>
      )}

      {booking.sketch_url && !isSketchPdf && (
        <Section title="Survey Sketch">
          <img
            src={resolveAssetUrl(booking.sketch_url)}
            alt="Survey sketch"
            className="max-h-[280px] max-w-full border border-black"
            data-testid="print-booking-sketch"
          />
        </Section>
      )}
      {booking.sketch_url && isSketchPdf && (
        <Section title="Survey Sketch">
          <div className="text-xs italic">Sketch attached: {booking.sketch_url}</div>
        </Section>
      )}

      <div className="mt-12 grid grid-cols-3 gap-8 text-xs">
        <Signature label="Customer Signature" />
        <Signature label="Channel Partner / Sales" name={booking.requested_by_name} />
        <Signature label="Admin / Super Admin" name={booking.approved_by_name} />
      </div>

      <div className="mt-8 pt-3 border-t border-black text-center text-[10px] text-gray-700">
        Computer-generated document · Printed {new Date().toLocaleString("en-IN")} · Booking {booking.booking_no}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] tracking-widest uppercase font-semibold border-b border-black pb-0.5 mb-1">{title}</div>
      <div className="text-xs">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-0.5">
      <div className="text-[10px] tracking-wider uppercase text-gray-700 col-span-1">{k}</div>
      <div className="col-span-2">{v}</div>
    </div>
  );
}
function Signature({ label, name }) {
  return (
    <div className="text-center">
      <div className="h-10 border-b border-black mb-1"></div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      {name && <div className="text-[10px] text-gray-700 mt-0.5">{name}</div>}
    </div>
  );
}
